import {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {Field, CoverUploader, UploadChip, GenreSelect} from "../../components/BookFormControls";
import {AudioTracksDraftList, type AudioTrackDraft} from "../../components/AudioTracksDraftList";
import {
    ArrowLeft,
    BookText, Check, CheckCircle2,
    Headphones,
    Info,
    Pencil,
    TriangleAlert,
    type LucideIcon,
} from "lucide-react";
import {
    Badge,
    Box,
    Button,
    Center,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Heading,
    Icon,
    IconButton,
    Input,
    SimpleGrid,
    Spacer,
    Spinner,
    Switch,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    Textarea,
    VStack,
    useToast,
} from "@chakra-ui/react";
import {useAuth} from "../../services/auth/AuthProvider";
import {
    createBook,
    generateBookDemo,
    packageEbook,
    uploadAudiobook,
    uploadBookAsset,
    uploadCover,
    type BookVersionPayload, UpdateBookPayload, getBookWithSiblings, updateBook, usePersistBothMutation,
    usePersistSingleMutation,
} from "../../services/api/books";
import {getGenres} from "../../services/api/genres";
import {HtmlEditorField} from "../../components/HtmlEditorField";
import {analyticsEvents} from "../../../../utilities/analyticsEvents";
import Seo from "../../../../ui/components/Seo/Seo";

const isoToDateInput = (iso: string) => (iso ? iso.slice(0, 10) : "");
const dateInputToIso = (v: string) =>
    v ? new Date(`${v}T00:00:00.000Z`).toISOString() : "";

/**
 * Flutter parity: the publisher types a UAH amount; `priceId` is the first tier
 * whose UAH price is GREATER than the amount (else the most expensive tier).
 * The typed amount itself is stored as `recommendedPrice`. Blank → no tier.
 */


const isNumericAmount = (s: string) => {
    const val = s.trim().replace(",", ".");
    if (val === "") return false;
    const num = Number(val);
    return Number.isFinite(num) && num >= 0;
};

const strings = {
    createBookFirstLabel: "Спочатку створіть книгу!",
    audiobookUploadedLabel: "Аудіокнигу завантажено",
    unexpectedErrorOccurred: "Сталась помилка!",
    chapterSavedLabel: "Назву розділу збережено!",
    bookCreatedLabel: "Книгу створено!",
    bookSavedLabel: "Книгу збережено!",
    changesSavedLabel: "Зміни збережено!",
    demoGeneratedLabel: "Демо згенеровано",
    demoNotGeneratedLabel: "Демо не згенеровано (закоротка книга або без змісту)",
    nameLabel: "Назва",
    authorLabel: "Автор",
    genresLabel: "Жанри",
    isbnLabel: "ISBN",
    priceLabel: "Ціна",
    coverLabel: "Обкладинка",
    chooseBookFileLabel: "Файл книги (epub)",
    audiobookCoverLabel: "Обкладинка аудіо",
    audiobookChaptersLabel: "Розділи (mp3)",
    enterValidPriceLabel: "Будь ласка, введіть правильну ціну",
    publishRequirementsLabel: "Заповніть:",
    publishedToastLabel: "Опубліковано та збережено",
    unpublishedToastLabel: "Знято з публікації",
    editLabel: "Редагувати",
    bookLabel: "Книжка",
    newBookLabel: "Нова книга",
    cancelLabel: "Скасувати",
    createLabel: "Створити",
    saveLabel: "Зберегти",
    generalInfoLabel: "Загальна інформація",
    authorsHintLabel: "Автори через кому",
    descriptionLabel: "Опис",
    descriptionHTMLLabel: "Опис HTML",
    descriptionHTMLHintLabel: "Детальний опис книги. Можна виділяти текст жирним, курсивом, підкресленням, робити списки та додавати посилання — це форматування побачить читач.",
    ebookLabel: "Електронна книга",
    audiobookLabel: "Аудіокнига",
    publicLabel: "Опубліковано",
    ebookHintLabel: "Файл книги, ліцензія та демо",
    audiobookHintLabel: "Окремі права на аудіоверсію",
    publishEbookLabel: "Опублікувати електронну книгу",
    publishAudiobookLabel: "Опублікувати аудіокнигу",
    saveBookFirstForUploadsLabel: "Файли (обкладинка, epub, ліцензія, демо) можна буде завантажити після створення книги — натисніть «Створити» вгорі.",
    chooseCoverLabel: "Завантажити обкладинку",
    isbnMinLengthLabel: "ISBN має містити щонайменше 10 символів",
    publishingDateLabel: "Дата публікації",
    rightsValidityTermLabel: "Термін дії прав",
    bookFileUploadedLabel: "Файл книги завантажено",
    replaceLabel: "Замінити",
    chooseLicenceLabel: "Завантажити ліцензію",
    demoLabel: "Демо",
    demoGeneratingLabel: "Демо генерується…",
    chooseDemoLabel: "Завантажити демо",
    showDemoLabel: "Показувати демо",
    viewLabel: "Переглянути",
    autoGeneratedLabel: "згенеровано автоматично",
    audiobookTracksLabel: "Доріжки аудіокниги",
    trackTitleLabel: "Назва доріжки",
    audiobookEditWarningLabel: "Доступне лише редагування назв розділів. Зміна складу аудіодоріжок потребує повного перезавантаження книги.",
    reuploadAudiobookLabel: "Завантажити аудіокнигу наново",
    newTracksLabel: "Нові доріжки для завантаження",
    audiobookOverwriteWarningLabel: "Зверніть увагу: після надсилання нові файли повністю замінять попередні, а старі стануть недійсними.",
    chooseOtherMp3Label: "Обрати інші mp3-файли",
    uploadToCloudLabel: "Надіслати в хмару",
    uploadMp3FilesLabel: "Завантажити mp3-файли для аудіокниги",
    descriptionMinLengthLabel: "Опис має містити щонайменше 5 символів",
    audioDemoHintLabel: "Короткий фрагмент для безкоштовного прослуховування покупцями. Тривалість обираєте ви.",
    unsavedAudioWarningTitle: "Нові аудіофайли завантажено, але зміни ще не збережено!",
    unsavedAudioWarningDesc: "Обов'язково збережіть книгу, щоб зафіксувати оновлений список аудіодоріжок.",
} as const;

export function BookEditPage() {
    const t = (key: keyof typeof strings) => strings[key];
    const navigate = useNavigate();
    const booksPath = "/publishers/panel/books";
    const toast = useToast();
    const queryClient = useQueryClient();
    const {id = ""} = useParams();
    const isNew = !id;
    const {publishingHouse} = useAuth();
    const publisherName = publishingHouse?.name ?? "";

    const genresQuery = useQuery(["genres"], getGenres);
    const allGenres = genresQuery.data ?? [];
    const siblingsQuery = useQuery(
        ["book", id, "siblings"],
        () => getBookWithSiblings(id),
        {enabled: !!id},
    );

    const MIN_DESCRIPTION_LENGTH = 5;
    const MAX_DESCRIPTION_LENGTH = 500;

    // form state
    const [name, setName] = useState("");
    const [authors, setAuthors] = useState("");
    const [isbn, setIsbn] = useState("");
    const [description, setDescription] = useState("");
    const [descriptionHTML, setDescriptionHTML] = useState("");
    const [recommendedPrice, setRecommendedPrice] = useState("");
    const [publishingDate, setPublishingDate] = useState("");
    const [rightsValidityTerm, setRightsValidityTerm] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [showDemo, setShowDemo] = useState(false);
    const [genres, setGenres] = useState<string[]>([]);
    const [cover, setCover] = useState("");
    const [lcpId, setLcpId] = useState("");
    const [licenseURL, setLicenseURL] = useState("");
    const [demoURL, setDemoURL] = useState("");
    const [audioCover, setAudioCover] = useState("");
    const [audioHasFile, setAudioHasFile] = useState(false);
    const [audioLcpId, setAudioLcpId] = useState("");
    const [audioIsbn, setAudioIsbn] = useState("");
    const [audioRecommendedPrice, setAudioRecommendedPrice] = useState("");
    const [audioPublishingDate, setAudioPublishingDate] = useState("");
    const [audioRightsValidityTerm, setAudioRightsValidityTerm] = useState("");
    const [audioIsPublic, setAudioIsPublic] = useState(false);
    const [audioDemoURL, setAudioDemoURL] = useState("");
    const [audioShowDemo, setAudioShowDemo] = useState(false);
    const [uploading, setUploading] = useState<string | null>(null);
    const [generatingDemo, setGeneratingDemo] = useState(false);
    const [audioChapters, setAudioChapters] = useState<string[]>([]);
    const [editingChapterIndex, setEditingChapterIndex] = useState<number | null>(null);
    const [savingChapterIndex, setSavingChapterIndex] = useState<number | null>(null);
    const [audioDrafts, setAudioDrafts] = useState<AudioTrackDraft[]>([]);
    const [isUploadingAudio, setIsUploadingAudio] = useState(false);
    const ebookDoc = siblingsQuery.data?.ebook;
    const audiobookDoc = siblingsQuery.data?.audiobook;
    const hasUnsavedAudio = Boolean(
        audioLcpId && audioLcpId !== (audiobookDoc?.lcpId ?? "")
    );

    useEffect(() => {
        if (!hasUnsavedAudio) return;
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasUnsavedAudio]);

    const [ebookId, setEbookId] = useState<string | undefined>();
    const [audiobookId, setAudiobookId] = useState<string | undefined>();
    const [tabIndex, setTabIndex] = useState(0);

    const book = siblingsQuery.data;
    useEffect(() => {
        if (!ebookDoc) return;
        setName(ebookDoc.name);
        setAuthors(ebookDoc.authors.join(", "));
        setIsbn(ebookDoc.isbn);
        setDescription(ebookDoc.description);
        setRecommendedPrice(ebookDoc.recommendedPrice);
        setPublishingDate(isoToDateInput(ebookDoc.publishingDate));
        setRightsValidityTerm(isoToDateInput(ebookDoc.rightsValidityTerm));
        setIsPublic(ebookDoc.isPublic);
        setShowDemo(ebookDoc.showDemo);
        setGenres(ebookDoc.genres);
        setCover(ebookDoc.cover);
        setLcpId(ebookDoc.lcpId || "");
        setLicenseURL(ebookDoc.licenseURL);
        setDemoURL(ebookDoc.demoURL);
        setDescriptionHTML(ebookDoc.descriptionHTML);
    }, [ebookDoc]);

    useEffect(() => {
        if (!audiobookDoc) return;
        // спільні поля дублюємо, лише якщо ebook ще не завантажений (щоб не перезаписати
        // те, що юзер уже побачив/поправив у ebook-таб)
        if (!ebookDoc) {
            setName(audiobookDoc.name);
            setAuthors(audiobookDoc.authors.join(", "));
            setGenres(audiobookDoc.genres);
            setDescriptionHTML(audiobookDoc.descriptionHTML);
        }
        setAudioCover(audiobookDoc.cover);
        setAudioHasFile(Boolean(audiobookDoc.lcpId));
        setAudioLcpId(audiobookDoc.lcpId ?? "");
        setAudioIsbn(audiobookDoc.isbn);
        setAudioRecommendedPrice(audiobookDoc.recommendedPrice);
        setAudioPublishingDate(isoToDateInput(audiobookDoc.publishingDate));
        setAudioRightsValidityTerm(isoToDateInput(audiobookDoc.rightsValidityTerm));
        setAudioIsPublic(audiobookDoc.isPublic);
        setAudioDemoURL(audiobookDoc.demoURL);
        setAudioShowDemo(Boolean(audiobookDoc.showDemo));
        setAudioChapters(Array.isArray(audiobookDoc.chapters) ? audiobookDoc.chapters : []);
    }, [audiobookDoc, ebookDoc]);

    const publishingHouseValue = ebookDoc?.publishingHouse ?? audiobookDoc?.publishingHouse ?? publisherName;

    function handlePickMp3s(e: React.ChangeEvent<HTMLInputElement>) {
        const rawFiles = Array.from(e.target.files || []);
        const files = rawFiles.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
        );
        if (files.length > 0) {
            setAudioDrafts(
                files.map((file, i) => ({
                    id: `${file.name}_${file.size}_${file.lastModified}_${i}_${Math.random().toString(36).slice(2, 7)}`,
                    file,
                    title: `Частина ${i + 1}`,
                }))
            );
        }
        e.target.value = "";
    }

    async function handleUploadAudio() {
        if (audioDrafts.length === 0) return;
        if (isNew) {
            toast({description: t("createBookFirstLabel"), status: "info"});
            return;
        }

        setIsUploadingAudio(true);
        try {
            const targetId = await ensureAudiobookId();
            const chapters = audioDrafts.map((d, i) => d.title.trim() || `Частина ${i + 1}`);
            const newLcpId = await uploadAudiobook({
                id: targetId,
                title: name || "Book",
                files: audioDrafts.map((d) => d.file),
                coverUrl: audioCover || undefined,
                author: authorList()[0] || "",
                language: "uk",
                chapters,
            });
            setAudioLcpId(newLcpId);
            setAudioHasFile(!!newLcpId);
            setAudioChapters(chapters);
            setAudioDrafts([]);
            toast({description: t("audiobookUploadedLabel"), status: "success"});
        } catch (err) {
            console.error(err);
            toast({description: t("unexpectedErrorOccurred"), status: "error"});
        } finally {
            setIsUploadingAudio(false);
        }
    }

    // The book is created with only an ebook version by default. The backend's
    // /updateBook auto-creates the audiobook sibling document the first time
    // it receives an audiobook-typed payload for an anchor that doesn't have
    // one yet. That covers handleSave / togglePublish / saveChapterTitle,
    // which all go through updateBook directly. File uploads (cover, mp3,
    // demo), however, hit separate endpoints that expect a real document id —
    // so before any of those we silently make sure the audiobook document
    // exists (creating it via updateBook if needed) and use its id. No button,
    // no banner — this just happens transparently on first use.
    async function ensureAudiobookId(): Promise<string> {
        if (audiobookId) return audiobookId;
        const res = await updateBook(ebookId ?? id, buildAudiobookUpdatePayload());
        setAudiobookId(res.id);
        queryClient.invalidateQueries(["book", id, "siblings"]);
        return res.id;
    }

    async function saveChapterTitle(i: number) {
        setSavingChapterIndex(i);
        try {
            const res = await updateBook(audiobookId ?? id, buildAudiobookUpdatePayload());
            setAudiobookId(res.id);
            setEditingChapterIndex(null);
            toast({description: t("chapterSavedLabel") || "Назву розділу збережено", status: "success"});
        } catch (err) {
            console.error(err);
            toast({description: t("unexpectedErrorOccurred"), status: "error"});
        } finally {
            setSavingChapterIndex(null);
        }
    }

    function buildEbookUpdatePayload(opts?: { isPublic?: boolean }): UpdateBookPayload {
        return {
            type: "ebook",
            name: name.trim(),
            authors: authorList(),
            genres,
            publishingHouse: publishingHouseValue,
            description: orUndef(description),
            descriptionHTML: orUndef(ebookDoc?.descriptionHTML || descriptionHTML),
            cover: orUndef(cover),
            ISBN: orUndef(isbn),
            recommendedPrice: orUndef(recommendedPrice.replace(",", ".")),
            publishingDate: publishingDate ? dateInputToIso(publishingDate) : undefined,
            rightsValidityTerm: rightsValidityTerm ? dateInputToIso(rightsValidityTerm) : undefined,
            showDemo,
            isPublic: opts?.isPublic ?? isPublic,
            lcpId: orUndef(lcpId),
            licenseURL: orUndef(licenseURL),
            demoURL: orUndef(demoURL),
        };
    }

    function buildAudiobookUpdatePayload(opts?: { isPublic?: boolean }): UpdateBookPayload {
        return {
            type: "audiobook",
            name: name.trim(),
            authors: authorList(),
            genres,
            publishingHouse: publishingHouseValue,
            description: orUndef(description),
            descriptionHTML: orUndef(descriptionHTML || description),
            cover: orUndef(audioCover),
            ISBN: orUndef(audioIsbn),
            recommendedPrice: orUndef(audioRecommendedPrice.replace(",", ".")),
            publishingDate: audioPublishingDate ? dateInputToIso(audioPublishingDate) : undefined,
            rightsValidityTerm: audioRightsValidityTerm ? dateInputToIso(audioRightsValidityTerm) : undefined,
            showDemo: audioShowDemo,
            isPublic: opts?.isPublic ?? audioIsPublic,
            demoURL: orUndef(audioDemoURL),
            lcpId: orUndef(audioLcpId),
            chapters: audioChapters,
        };
    }

    const orUndef = (v: string | undefined | null): string | undefined =>
        v && v.trim() !== "" ? v.trim() : undefined;

    const persistSingle = usePersistSingleMutation(id);
    const persistBoth = usePersistBothMutation(id);


    const createMutation = useMutation(createBook, {
        onSuccess: (ids) => {
            toast({description: t("bookCreatedLabel"), status: "success"});
            analyticsEvents.addBook(ids[0], name.trim(), publisherName);
            navigate(`${booksPath}/${encodeURIComponent(ids[0])}/edit`, {replace: true});
        },
        onError: () => {
            toast({description: t("unexpectedErrorOccurred"), status: "error"});
        },
    });

// Uploads need an existing book id — block them until the book is created.
    async function doUpload(
        kind: string,
        fn: () => Promise<string>,
        set: (url: string) => void,
    ) {
        if (isNew) {
            toast({description: t("createBookFirstLabel"), status: "info"});
            return;
        }
        setUploading(kind);
        try {
            const res = await fn();
            if (res) {
                set(res);
                toast({description: t("changesSavedLabel") || "Зміни збережено", status: "success"});
            }
        } catch (err) {
            console.error("doUpload Error:", err);
            toast({description: t("unexpectedErrorOccurred"), status: "error"});
        } finally {
            setUploading(null);
        }
    }

    // Demo is (re)generated in the background right after an epub upload.
    async function startDemoGeneration(file: File) {
        if (isNew) return;
        setGeneratingDemo(true);
        setDemoURL("");
        try {
            const url = await generateBookDemo(ebookId ?? id, file);
            if (url) {
                setDemoURL(url);
                toast({description: t("demoGeneratedLabel"), status: "success"});
            } else {
                toast({
                    description: t("demoNotGeneratedLabel"),
                    status: "warning",
                    duration: 6000,
                    isClosable: true,
                });
            }
        } catch {
            toast({
                description: t("demoNotGeneratedLabel"),
                status: "warning",
                duration: 6000,
                isClosable: true,
            });
        } finally {
            setGeneratingDemo(false);
        }
    }

    const authorList = () =>
        authors.split(",").map((a) => a.trim()).filter(Boolean);

    // Shared metadata required to publish either format.
    function sharedMissing(): string[] {
        const m: string[] = [];
        if (!name.trim()) m.push(t("nameLabel"));
        if (authorList().length === 0) m.push(t("authorLabel"));
        if (genres.length === 0) m.push(t("genresLabel"));
        if (description.trim().length < MIN_DESCRIPTION_LENGTH) m.push(t("descriptionLabel"));
        return m;
    }

    function ebookMissing(): string[] {
        const m = sharedMissing();
        if (!isValidIsbn(isbn)) m.push(t("isbnLabel"));
        if (!isNumericAmount(recommendedPrice)) m.push(t("priceLabel"));
        if (!cover) m.push(t("coverLabel"));
        if (!lcpId) m.push(t("chooseBookFileLabel"));
        return m;
    }

    function audioMissing(): string[] {
        const m = sharedMissing();
        if (!isValidIsbn(audioIsbn)) m.push(t("isbnLabel"));
        if (!isNumericAmount(audioRecommendedPrice)) m.push(t("priceLabel"));
        if (!audioCover) m.push(t("audiobookCoverLabel"));
        if (!audioHasFile) m.push(t("audiobookChaptersLabel"));
        return m;
    }

    const isValidIsbn = (s: string) => s.trim().length >= 10;

    // Section-scoped payloads (files are saved separately by the upload methods,
    // so these carry only the text/select fields of each section).
    // Correctness: a typed price must be a number (the only hard save rule).
    function priceOk(): boolean {
        if (
            (recommendedPrice.trim() && !isNumericAmount(recommendedPrice)) ||
            (audioRecommendedPrice.trim() && !isNumericAmount(audioRecommendedPrice))
        ) {
            toast({description: t("enterValidPriceLabel"), status: "error"});
            return false;
        }
        return true;
    }

    // Create the book with only the ebook version — the audiobook version is
    // added lazily (see ensureAudiobookId) the moment the user actually
    // starts working with the Audiobook tab, not upfront.
    // Note: we deliberately do NOT require any ebook field to be filled here —
    // files (cover, epub, demo) can only be uploaded once the book has an id,
    // so blocking creation on "fields filled" would create a deadlock for
    // anyone who wants to start from a file upload. Full completeness (ISBN,
    // price, cover, file, etc.) is still enforced later, when actually
    // publishing a format via togglePublish.
    function handleCreate() {
        const missing = sharedMissing();
        if (missing.length) {
            toast({
                title: t("publishRequirementsLabel"),
                description: missing.join(", "),
                status: "warning",
                duration: 6000,
                isClosable: true,
            });
            return;
        }

        if (!priceOk()) return;

        const versions: BookVersionPayload[] = [
            {
                type: "ebook",
                ISBN: isbn.trim() || undefined,
                recommendedPrice: recommendedPrice.replace(",", ".").trim() || undefined,
                publishingDate: publishingDate ? dateInputToIso(publishingDate) : undefined,
                rightsValidityTerm: rightsValidityTerm ? dateInputToIso(rightsValidityTerm) : undefined,
                isPublic: false,
                showDemo: false,
            },
        ];

        createMutation.mutate({
            name: name.trim(),
            authors: authorList(),
            description: description.trim() || undefined,
            descriptionHTML: descriptionHTML.trim() || undefined,
            publishingHouse: publisherName || undefined,
            genres,
            versions,
        });
    }


    useEffect(() => {
        setEbookId(ebookDoc?.id);
        setAudiobookId(audiobookDoc?.id);
    }, [ebookDoc?.id, audiobookDoc?.id]);

    // Open the tab matching the format the user actually clicked into from the
    // list (the route `id` is that document's id — ebook or audiobook).
    const didSetInitialTab = useRef(isNew);

    useEffect(() => {
        if (!id || didSetInitialTab.current) return;
        if (!ebookDoc && !audiobookDoc) return; // ще не завантажилось

        if (audiobookDoc?.id === id) {
            setTabIndex(1);
        } else if (ebookDoc?.id === id) {
            setTabIndex(0);
        } else if (audiobookDoc && !ebookDoc) {
            setTabIndex(1);
        }
        didSetInitialTab.current = true;
    }, [id, ebookDoc, audiobookDoc]);

    // Save the draft (persists current state, keeping publish flags as they are).
    // Always touches both formats — if the audiobook document doesn't exist
    // yet, /updateBook creates it automatically (see ensureAudiobookId above).
    function handleSave() {
        if (!priceOk()) return;

        persistBoth.mutate(
            async () => {
                const ebookRes = await updateBook(ebookId ?? id, buildEbookUpdatePayload());
                const audioRes = await updateBook(audiobookId ?? id, buildAudiobookUpdatePayload());
                return {ebookRes, audioRes};
            },
            {
                onSuccess: ({ebookRes, audioRes}) => {
                    toast({description: t("bookSavedLabel"), status: "success"});
                    setEbookId(ebookRes.id);
                    setAudiobookId(audioRes.id);
                    queryClient.invalidateQueries(["book", id, "siblings"]);
                },
                onError: () => {
                    toast({description: t("unexpectedErrorOccurred"), status: "error"});
                },
            },
        );
    }

    function handleSaveOrCreate() {
        if (isNew) handleCreate();
        else handleSave();
    }

    // Publish/unpublish a format. Publishing validates completeness, then SAVES
    // immediately (publish = save + publish) so the user needn't press Save.
    function togglePublish(format: "ebook" | "audio", on: boolean) {
        if (isNew) {
            toast({description: t("createBookFirstLabel"), status: "info"});
            return;
        }
        if (on) {
            const missing = format === "ebook" ? ebookMissing() : audioMissing();
            if (missing.length) {
                toast({
                    title: t("publishRequirementsLabel"),
                    description: missing.join(", "),
                    status: "warning",
                    duration: 6000,
                    isClosable: true,
                });
                return;
            }
        }
        if (!priceOk()) return;

        if (format === "ebook") setIsPublic(on);
        else setAudioIsPublic(on);

        const targetId = format === "ebook" ? (ebookId ?? id) : (audiobookId ?? id);
        const fn = format === "ebook"
            ? () => updateBook(targetId, buildEbookUpdatePayload({isPublic: on}))
            : () => updateBook(targetId, buildAudiobookUpdatePayload({isPublic: on}));

        persistSingle.mutate(fn, {
            onSuccess: (res) => {
                if (format === "ebook") setEbookId(res.id);
                else setAudiobookId(res.id);
                toast({description: on ? t("publishedToastLabel") : t("unpublishedToastLabel"), status: "success"});
            },
            onError: () => {
                if (format === "ebook") setIsPublic(!on);
                else setAudioIsPublic(!on);
                toast({description: t("unexpectedErrorOccurred"), status: "error"});
            },
        });
    }

    if (!isNew && siblingsQuery.isLoading) {
        return (
            <Center h="100vh">
                <Spinner color="brand.orange" size="xl"/>
            </Center>
        );
    }
    if (!isNew && (siblingsQuery.isError || !book)) {
        return (
            <Center h="100vh">
                <Text color="gray.500">{t("unexpectedErrorOccurred")}</Text>
            </Center>
        );
    }

    const busy = persistSingle.isLoading || persistBoth.isLoading || createMutation.isLoading || uploading !== null;

    const pageTitle = isNew
        ? "Створення книги — Кабінет видавця"
        : `${name.trim() || "Редагування книги"} — Кабінет видавця`;
    const pageDescription = isNew
        ? "Створення нової книги в кабінеті видавця"
        : "Редагування книги в кабінеті видавця";

    return (
        <Flex direction="column" minH="100%">
            <Seo
                title={pageTitle}
                description={pageDescription}
                noindex
            />
            <HStack
                as="header"
                position="sticky"
                top={0}
                zIndex={20}
                bg="whiteAlpha.900"
                backdropFilter="blur(6px)"
                borderBottom="1px solid"
                borderColor="blackAlpha.100"
                px={6}
                py={3}
                spacing={3}
            >
                <IconButton
                    aria-label="back"
                    size="sm"
                    variant="ghost"
                    icon={<Icon as={ArrowLeft} boxSize={4}/>}
                    onClick={() => navigate(booksPath)}
                />
                <Heading size="md" flex={1} noOfLines={1} textTransform="uppercase" letterSpacing="wide">
                    {isNew ? t("newBookLabel") : `${t("editLabel")} — ${name || t("bookLabel")}`}
                </Heading>
                <Button variant="outline" onClick={() => navigate(booksPath)} isDisabled={busy}>
                    {t("cancelLabel")}
                </Button>
                <Button
                    onClick={handleSaveOrCreate}
                    isLoading={isNew ? createMutation.isLoading : siblingsQuery.isLoading}
                    isDisabled={uploading !== null}
                >
                    {isNew ? t("createLabel") : t("saveLabel")}
                </Button>
            </HStack>

            <VStack align="stretch" spacing={6} p={6}>
                {/* General — shared metadata */}
                <Section icon={Info} title={t("generalInfoLabel")}>
                    <SimpleGrid columns={{base: 1, sm: 2}} spacing={4}>
                        <Field label={t("nameLabel")} span={2}>
                            <Input value={name} onChange={(e) => setName(e.target.value)}/>
                        </Field>
                        <Field label={t("authorLabel")} span={2}>
                            <Input
                                value={authors}
                                onChange={(e) => setAuthors(e.target.value)}
                                placeholder={t("authorsHintLabel")}
                            />
                        </Field>
                    </SimpleGrid>

                    <Field
                        label={t("descriptionLabel")}
                        isInvalid={description.trim().length > 0 && description.trim().length < MIN_DESCRIPTION_LENGTH}
                        errorText={t("descriptionMinLengthLabel")}
                    >
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
                            maxLength={MAX_DESCRIPTION_LENGTH}
                            rows={4}
                        />
                        <Text
                            fontSize="xs"
                            color={
                                description.trim().length > 0 && description.trim().length < MIN_DESCRIPTION_LENGTH
                                    ? "red.500"
                                    : "gray.500"
                            }
                            textAlign="right"
                            mt={1}
                        >
                            {description.length}/{MAX_DESCRIPTION_LENGTH}
                            {description.trim().length > 0 && description.trim().length < MIN_DESCRIPTION_LENGTH &&
                                ` (мінімум ${MIN_DESCRIPTION_LENGTH})`}
                        </Text>
                    </Field>
                    <HtmlEditorField
                        label={t("descriptionHTMLLabel")}
                        value={descriptionHTML}
                        onChange={setDescriptionHTML}
                        placeholder={t("descriptionHTMLHintLabel")}
                    />

                    <Field label={t("genresLabel")}>
                        <GenreSelect all={allGenres} value={genres} onChange={setGenres} label={t("genresLabel")}/>
                    </Field>
                </Section>

                {/* Formats — e-book / audiobook as tabs, always both */}
                <Tabs variant="line" colorScheme="orange" index={tabIndex} onChange={setTabIndex}>
                    <TabList>
                        <Tab gap={2} fontWeight="semibold" fontSize="md" py={3}>
                            {t("ebookLabel")}
                            {isPublic && (
                                <Badge colorScheme="green" borderRadius="full" px={2} textTransform="none">
                                    {t("publicLabel")}
                                </Badge>
                            )}
                        </Tab>
                        <Tab gap={2} fontWeight="semibold" fontSize="md" py={3}>
                            {t("audiobookLabel")}
                            {audioIsPublic && (
                                <Badge colorScheme="green" borderRadius="full" px={2} textTransform="none">
                                    {t("publicLabel")}
                                </Badge>
                            )}
                        </Tab>
                    </TabList>
                    <TabPanels>
                        <TabPanel px={0} pt={4}>
                            {/* E-book — own cover / price / date / public */}
                            <Section
                                icon={BookText}
                                title={t("ebookLabel")}
                                subtitle={t("ebookHintLabel")}
                                published={isPublic}
                                headerRight={
                                    <FormControl display="flex" alignItems="center" w="fit-content">
                                        <FormLabel mb={0} mr={2} fontSize="sm" fontWeight="medium">
                                            {t("publishEbookLabel")}
                                        </FormLabel>
                                        <Switch isChecked={isPublic}
                                                onChange={(e) => togglePublish("ebook", e.target.checked)}
                                                colorScheme="green"/>
                                    </FormControl>
                                }
                            >
                                {isNew && (
                                    <Box p={3} bg="blue.50" border="1px solid" borderColor="blue.200"
                                         borderRadius="md">
                                        <HStack align="flex-start" spacing={2}>
                                            <Icon as={Info} color="blue.500" mt={0.5} boxSize={4}/>
                                            <Text fontSize="xs">
                                                {t("saveBookFirstForUploadsLabel") || "Файли (обкладинка, epub, ліцензія, демо) можна буде завантажити після створення книги — натисніть «Створити» вгорі."}
                                            </Text>
                                        </HStack>
                                    </Box>
                                )}
                                <Flex direction={{base: "column", md: "row"}} gap={6}>
                                    <VStack flexShrink={0} spacing={3} align="center">
                                        <UploadGate disabled={isNew}>
                                            <CoverUploader
                                                url={cover}
                                                busy={uploading === "cover"}
                                                label={t("chooseCoverLabel")}
                                                onPick={(f) => doUpload("cover", () => uploadCover(id, f), setCover)}/>
                                        </UploadGate>
                                    </VStack>
                                    <VStack flex={1} align="stretch" spacing={4}>
                                        <Field label={t("isbnLabel")}
                                               isInvalid={isbn.trim() !== "" && !isValidIsbn(isbn)}
                                               errorText={t("isbnMinLengthLabel")}>
                                            <Input value={isbn} onChange={(e) => setIsbn(e.target.value)}/>
                                        </Field>
                                        <Field
                                            label={t("priceLabel")}
                                            isInvalid={recommendedPrice.trim() !== "" && !isNumericAmount(recommendedPrice)}
                                            errorText={t("enterValidPriceLabel")}
                                        >
                                            <Input
                                                type="number"
                                                inputMode="decimal"
                                                min="0"
                                                step="any"
                                                maxW="180px"
                                                value={recommendedPrice}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (!val.startsWith("-")) {
                                                        setRecommendedPrice(val);
                                                    }
                                                }}
                                                placeholder="₴"
                                            />
                                        </Field>
                                        <SimpleGrid columns={{base: 1, sm: 2}} spacing={4}>
                                            <Field label={t("publishingDateLabel")}>
                                                <Input type="date" value={publishingDate}
                                                       onChange={(e) => setPublishingDate(e.target.value)}/>
                                            </Field>
                                            <Field label={t("rightsValidityTermLabel")}>
                                                <Input type="date" value={rightsValidityTerm}
                                                       onChange={(e) => setRightsValidityTerm(e.target.value)}/>
                                            </Field>
                                        </SimpleGrid>
                                    </VStack>
                                </Flex>

                                <UploadGate disabled={isNew}>
                                    <SimpleGrid columns={{base: 1, sm: 2}} spacing={3}>
                                        {lcpId ? (
                                            <HStack
                                                p={3}
                                                borderWidth="1px"
                                                borderColor="green.300"
                                                bg="green.50"
                                                borderRadius="md"
                                                justify="space-between"
                                            >
                                                <HStack spacing={2}>
                                                    <Icon as={CheckCircle2} color="green.500" boxSize={5}/>
                                                    <VStack align="start" spacing={0}>
                                                        <Text fontSize="sm" fontWeight="medium" color="green.900">
                                                            {t("bookFileUploadedLabel") || "EPUB файл упаковано"}
                                                        </Text>
                                                        <Text fontSize="xs" color="green.700" fontFamily="mono">
                                                            ID: {lcpId}
                                                        </Text>
                                                    </VStack>
                                                </HStack>

                                                {/* Кнопка для перезавантаження файла якщо треба замінити */}
                                                <UploadChip
                                                    label={t("replaceLabel") || "Замінити"}
                                                    done={false}
                                                    busy={uploading === "epub"}
                                                    accept=".epub,application/epub+zip"
                                                    onPick={(f) => {
                                                        const safeTitle = (name || "Book").replace(/[/\\?%*:|"<>]/g, " ").trim();
                                                        doUpload("epub", () => packageEbook(f, safeTitle), setLcpId);
                                                        startDemoGeneration(f);
                                                    }}
                                                />
                                            </HStack>
                                        ) : (
                                            <UploadChip
                                                label={t("chooseBookFileLabel")}
                                                done={false}
                                                busy={uploading === "epub"}
                                                accept=".epub,application/epub+zip"
                                                onPick={(f) => {
                                                    const safeTitle = (name || "Book").replace(/[/\\?%*:|"<>]/g, " ").trim();
                                                    doUpload("epub", () => packageEbook(f, safeTitle), setLcpId);
                                                    startDemoGeneration(f);
                                                }}
                                            />
                                        )}
                                        <UploadChip
                                            label={t("chooseLicenceLabel")}
                                            done={!!licenseURL}
                                            busy={uploading === "license"}
                                            onPick={(f) => doUpload("license", () => uploadBookAsset(ebookId ?? id, "licenseURL", f), setLicenseURL)}/>
                                    </SimpleGrid>
                                </UploadGate>

                                {/* Demo — a separate, unencrypted epub sample + visibility toggle */}
                                <UploadGate disabled={isNew}>
                                    <Box borderWidth="1px" borderColor="blackAlpha.200" borderRadius="lg"
                                         bg="blackAlpha.50" p={4}>
                                        <Text fontSize="sm" fontWeight="semibold" mb={3}>{t("demoLabel")}</Text>
                                        <Flex gap={3} wrap="wrap" align="center">
                                            {generatingDemo ? (
                                                <Button variant="outline" borderStyle="dashed" isDisabled
                                                        leftIcon={<Spinner size="sm"/>}>
                                                    {t("demoGeneratingLabel")}
                                                </Button>
                                            ) : (
                                                <UploadChip
                                                    label={t("chooseDemoLabel")}
                                                    done={!!demoURL}
                                                    busy={uploading === "demo"}
                                                    accept=".epub,application/epub+zip"
                                                    onPick={(f) => doUpload("demo", () => uploadBookAsset(ebookId ?? id, "demoURL", f), setDemoURL)}
                                                />
                                            )}
                                            <FormControl display="flex" alignItems="center" w="fit-content">
                                                <Switch isChecked={showDemo}
                                                        onChange={(e) => setShowDemo(e.target.checked)}
                                                        isDisabled={!demoURL}
                                                        mr={2}
                                                        colorScheme="orange"/>
                                                <FormLabel mb={0} fontSize="sm">{t("showDemoLabel")}</FormLabel>
                                            </FormControl>
                                            {demoURL && (
                                                <Button as="a" href={demoURL} target="_blank" rel="noopener"
                                                        variant="link" colorScheme="orange" size="sm">
                                                    {t("viewLabel")}
                                                </Button>
                                            )}
                                            {demoURL.includes("/demo/") && (
                                                <Badge colorScheme="purple"
                                                       textTransform="none">{t("autoGeneratedLabel")}</Badge>
                                            )}
                                        </Flex>
                                    </Box>
                                </UploadGate>
                            </Section>
                        </TabPanel>
                        <TabPanel px={0} pt={4}>
                            {/* Audiobook — own cover / price / date / public */}
                            <Section
                                icon={Headphones}
                                title={t("audiobookLabel")}
                                subtitle={t("audiobookHintLabel")}
                                published={audioIsPublic}
                                headerRight={
                                    <FormControl display="flex" alignItems="center" w="fit-content">
                                        <FormLabel mb={0} mr={2} fontSize="sm" fontWeight="medium">
                                            {t("publishAudiobookLabel")}
                                        </FormLabel>
                                        <Switch isChecked={audioIsPublic}
                                                onChange={(e) => togglePublish("audio", e.target.checked)}
                                                colorScheme="green"/>
                                    </FormControl>
                                }
                            >
                                {isNew && (
                                    <Box p={3} bg="blue.50" border="1px solid" borderColor="blue.200"
                                         borderRadius="md">
                                        <HStack align="flex-start" spacing={2}>
                                            <Icon as={Info} color="blue.500" mt={0.5} boxSize={4}/>
                                            <Text fontSize="xs">
                                                {t("saveBookFirstForUploadsLabel") || "Спочатку натисніть «Створити» вгорі, щоб з'явилась книга."}
                                            </Text>
                                        </HStack>
                                    </Box>
                                )}
                                <Flex direction={{base: "column", md: "row"}} gap={6}>
                                    <VStack flexShrink={0} spacing={3} align="center">
                                        <UploadGate disabled={isNew}>
                                            <CoverUploader
                                                url={audioCover}
                                                busy={uploading === "audioCover"}
                                                label={t("audiobookCoverLabel")}
                                                onPick={(f) => doUpload("audioCover", async () => uploadBookAsset(await ensureAudiobookId(), "cover", f), setAudioCover)}
                                            />
                                        </UploadGate>
                                    </VStack>
                                    <VStack flex={1} align="stretch" spacing={4}>
                                        <Field label={t("isbnLabel")}
                                               isInvalid={audioIsbn.trim() !== "" && !isValidIsbn(audioIsbn)}
                                               errorText={t("isbnMinLengthLabel")}>
                                            <Input value={audioIsbn} onChange={(e) => setAudioIsbn(e.target.value)}/>
                                        </Field>
                                        <Field
                                            label={t("priceLabel")}
                                            isInvalid={audioRecommendedPrice.trim() !== "" && !isNumericAmount(audioRecommendedPrice)}
                                            errorText={t("enterValidPriceLabel")}
                                        >
                                            <Input
                                                type="number"
                                                inputMode="decimal"
                                                min="0"
                                                step="any"
                                                maxW="180px"
                                                value={audioRecommendedPrice}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (!val.startsWith("-")) {
                                                        setAudioRecommendedPrice(val);
                                                    }
                                                }}
                                                placeholder="₴"
                                            />
                                        </Field>
                                        <SimpleGrid columns={{base: 1, sm: 2}} spacing={4}>
                                            <Field label={t("publishingDateLabel")}>
                                                <Input type="date" value={audioPublishingDate}
                                                       onChange={(e) => setAudioPublishingDate(e.target.value)}/>
                                            </Field>
                                            <Field label={t("rightsValidityTermLabel")}>
                                                <Input type="date" value={audioRightsValidityTerm}
                                                       onChange={(e) => setAudioRightsValidityTerm(e.target.value)}/>
                                            </Field>
                                        </SimpleGrid>
                                    </VStack>
                                </Flex>

                                {/* Existing chapters read-only */}
                                {audioChapters.length > 0 && audioDrafts.length === 0 && (
                                    <Box p={4} borderRadius="lg" border="1px solid" borderColor="blackAlpha.100"
                                         bg="blackAlpha.50">
                                        {hasUnsavedAudio && (
                                            <Box
                                                p={3.5}
                                                mb={4}
                                                bg="orange.50"
                                                border="1px solid"
                                                borderColor="orange.300"
                                                borderRadius="md"
                                                boxShadow="xs"
                                            >
                                                <HStack align="flex-start" spacing={3}>
                                                    <Icon as={TriangleAlert} color="orange.500" boxSize={5} mt={0.5} flexShrink={0}/>
                                                    <VStack align="stretch" spacing={0.5}>
                                                        <Text fontSize="sm" fontWeight="bold" color="orange.900">
                                                            {t("unsavedAudioWarningTitle")}
                                                        </Text>
                                                        <Text fontSize="xs" color="orange.800">
                                                            {t("unsavedAudioWarningDesc")}
                                                        </Text>
                                                    </VStack>
                                                </HStack>
                                            </Box>
                                        )}
                                        <Text fontWeight="bold" mb={2}>
                                            {t("audiobookTracksLabel")} ({audioChapters.length}):
                                        </Text>
                                        <VStack align="stretch" spacing={2} mb={4}>
                                            {audioChapters.map((title, i) => {
                                                const isEditing = editingChapterIndex === i;
                                                const isSaving = savingChapterIndex === i;
                                                return (
                                                    <HStack key={i} spacing={3}>
                                                        <Text fontSize="xs" w="20px" flexShrink={0}>{i + 1}.</Text>
                                                        {isEditing ? (
                                                            <Input
                                                                size="sm"
                                                                bg="white"
                                                                autoFocus
                                                                value={title}
                                                                onChange={(e) => {
                                                                    const next = [...audioChapters];
                                                                    next[i] = e.target.value;
                                                                    setAudioChapters(next);
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") saveChapterTitle(i);
                                                                    if (e.key === "Escape") setEditingChapterIndex(null);
                                                                }}
                                                                placeholder={t("trackTitleLabel")}
                                                            />
                                                        ) : (
                                                            <Text fontSize="sm" flex={1}>{title}</Text>
                                                        )}
                                                        <IconButton
                                                            aria-label={isEditing ? (t("saveLabel") || "Зберегти") : (t("editLabel") || "Редагувати")}
                                                            size="sm"
                                                            variant="ghost"
                                                            colorScheme={isEditing ? "green" : "gray"}
                                                            isLoading={isSaving}
                                                            icon={<Icon as={isEditing ? Check : Pencil} boxSize={4}/>}
                                                            onClick={() => {
                                                                if (isEditing) saveChapterTitle(i);
                                                                else setEditingChapterIndex(i);
                                                            }}
                                                        />
                                                    </HStack>
                                                );
                                            })}
                                        </VStack>
                                        <Box p={3} bg="orange.50" border="1px solid" borderColor="orange.200"
                                             borderRadius="md" mb={3}>
                                            <HStack align="flex-start" spacing={2}>
                                                <Icon as={Info} color="orange.500" mt={0.5}/>
                                                <Text fontSize="xs">{t("audiobookEditWarningLabel")}</Text>
                                            </HStack>
                                        </Box>
                                        <Button size="sm"
                                                onClick={() => document.getElementById("audio-picker")?.click()}>
                                            {t("reuploadAudiobookLabel")}
                                        </Button>
                                    </Box>
                                )}

                                {/* New tracks editor */}
                                {audioDrafts.length > 0 && (
                                    <Box p={4} borderRadius="lg" border="1px solid" borderColor="brand.orange"
                                         bg="orange.50">
                                        {audioHasFile && (
                                            <Text fontSize="xs" color="red.600" mb={3}>
                                                {t("audiobookOverwriteWarningLabel")}
                                            </Text>
                                        )}
                                        <AudioTracksDraftList
                                            tracks={audioDrafts}
                                            onChange={setAudioDrafts}
                                            isDisabled={isUploadingAudio}
                                        />
                                        <HStack mt={4} spacing={3} wrap="wrap">
                                            <Button size="sm"
                                                    onClick={() => document.getElementById("audio-picker")?.click()}
                                                    isDisabled={isUploadingAudio}>
                                                {t("chooseOtherMp3Label")}
                                            </Button>
                                            <Button size="sm" colorScheme="orange" onClick={handleUploadAudio}
                                                    isLoading={isUploadingAudio}>
                                                {t("uploadToCloudLabel")}
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => {
                                                setAudioDrafts([]);
                                            }} isDisabled={isUploadingAudio}>
                                                {t("cancelLabel")}
                                            </Button>
                                        </HStack>
                                    </Box>
                                )}

                                {audioChapters.length === 0 && audioDrafts.length === 0 && (
                                    <UploadGate disabled={isNew}>
                                        <Button
                                            variant="outline"
                                            borderStyle="dashed"
                                            leftIcon={<Icon as={Headphones} boxSize={4}/>}
                                            onClick={() => document.getElementById("audio-picker")?.click()}
                                            isLoading={isUploadingAudio}
                                        >
                                            {t("uploadMp3FilesLabel")}
                                        </Button>
                                    </UploadGate>
                                )}
                                <input id="audio-picker" type="file" multiple accept="audio/mpeg" hidden
                                       onChange={handlePickMp3s}/>

                                {/* Audio demo — a separate, unencrypted sample + visibility toggle */}
                                <UploadGate disabled={isNew}>
                                    <Box borderWidth="1px" borderColor="blackAlpha.200" borderRadius="lg"
                                         bg="blackAlpha.50" p={4}>
                                        <Text fontSize="sm" fontWeight="semibold">{t("demoLabel")}</Text>
                                        <Text fontSize="xs" color="gray.500" mb={3}>{t("audioDemoHintLabel")}</Text>
                                        <Flex gap={3} wrap="wrap" align="center">
                                            <UploadChip
                                                label={t("chooseDemoLabel")}
                                                done={!!audioDemoURL}
                                                busy={uploading === "audioDemo"}
                                                accept="audio/*"
                                                onPick={(f) => doUpload("audioDemo", async () => uploadBookAsset(await ensureAudiobookId(), "demoURL", f), setAudioDemoURL)}
                                            />
                                            <FormControl display="flex" alignItems="center" w="fit-content">
                                                <Switch isChecked={audioShowDemo}
                                                        onChange={(e) => setAudioShowDemo(e.target.checked)}
                                                        isDisabled={!audioDemoURL}
                                                        mr={2}
                                                        colorScheme="orange"/>
                                                <FormLabel mb={0} fontSize="sm">{t("showDemoLabel")}</FormLabel>
                                            </FormControl>
                                            {audioDemoURL && (
                                                <Button as="a" href={audioDemoURL} target="_blank" rel="noopener"
                                                        variant="link" colorScheme="orange" size="sm">
                                                    {t("viewLabel")}
                                                </Button>
                                            )}
                                        </Flex>
                                    </Box>
                                </UploadGate>
                            </Section>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </VStack>
        </Flex>
    );
}

function UploadGate({disabled, children}: { disabled: boolean; children: React.ReactNode }) {
    if (!disabled) return <>{children}</>;
    return (
        <Box opacity={0.45} pointerEvents="none" cursor="not-allowed" userSelect="none">
            {children}
        </Box>
    );
}

function Section({
                     icon,
                     title,
                     subtitle,
                     published,
                     headerRight,
                     children,
                 }: {
    icon: LucideIcon;
    title: string;
    subtitle?: string;
    published?: boolean;
    headerRight?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <Box
            bg={published ? "green.50" : "white"}
            borderRadius="xl"
            border="1px solid"
            borderColor={published ? "green.300" : "blackAlpha.100"}
            boxShadow="sm"
            transition="background 0.15s, border-color 0.15s"
        >
            <HStack
                spacing={3}
                px={6}
                py={4}
                borderBottom="1px solid"
                borderColor={published ? "green.200" : "blackAlpha.100"}
            >
                <Center
                    boxSize={9}
                    borderRadius="lg"
                    bg={published ? "green.100" : "orange.50"}
                    color={published ? "green.600" : "brand.orange"}
                >
                    <Icon as={icon} boxSize={4}/>
                </Center>
                <Box>
                    <Heading size="sm" textTransform="uppercase" letterSpacing="wide">{title}</Heading>
                    {subtitle && <Text fontSize="xs" color="gray.500">{subtitle}</Text>}
                </Box>
                <Spacer/>
                {headerRight}
            </HStack>
            <VStack align="stretch" spacing={5} p={6}>
                {children}
            </VStack>
        </Box>
    );
}
